import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Chip, FormControl, InputLabel, Select, MenuItem, Grid, Alert, Switch, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';

const API = process.env.REACT_APP_API_URL || '';

const estiloTabla = {
  bgcolor: 'var(--c-superficie)',
  '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' }
};
const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
};
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };
const inputStyle = { style: { color: '#e0e0e0' } };

const ROL_COLORS = { admin: 'error', recepcionista: 'info', medico: 'success' };
const ROL_LABELS = { admin: 'Administrador', recepcionista: 'Recepcionista', medico: 'Médico' };

function estadoOnline(ultimaActividad) {
  if (!ultimaActividad) return null;
  const diff = Date.now() - new Date(ultimaActividad).getTime();
  const minutos = Math.floor(diff / 60000);
  if (minutos <= 8)  return { online: true,  label: 'En línea ahora',         color: '#66bb6a' };
  if (minutos <= 30) return { online: false, label: `Hace ${minutos} min`,    color: '#ffb74d' };
  const horas = Math.floor(minutos / 60);
  if (horas < 24)    return { online: false, label: `Hace ${horas}h`,         color: '#90a4ae' };
  const dias = Math.floor(horas / 24);
  return               { online: false, label: `Hace ${dias}d`,               color: '#616161' };
}

function fmtFechaHora(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const FORM_VACIO = { nombre: '', email: '', password: '', rol: 'recepcionista', medico_id: '' };

export default function Staff() {
  const [staff, setStaff]       = useState([]);
  const [medicos, setMedicos]   = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm]         = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');
  const miId = (() => {
    try { return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).id; } catch { return null; }
  })();

  useEffect(() => {
    cargarTodo();
    const iv = setInterval(cargarTodo, 30000); // refresca cada 30s para ver cambios de online
    return () => clearInterval(iv);
  }, []);

  async function cargarTodo() {
    try {
      const [staffR, medicosR] = await Promise.all([
        axios.get(`${API}/api/staff`),
        axios.get(`${API}/api/medicos`),
      ]);
      setStaff(staffR.data || []);
      setMedicos(medicosR.data || []);
    } catch (err) { console.error(err); }
  }

  function abrirNuevo() {
    setEditando(null);
    setForm(FORM_VACIO);
    setError('');
    setDialogOpen(true);
  }

  function abrirEditar(s) {
    setEditando(s.id);
    setForm({ nombre: s.nombre, email: s.email, password: '', rol: s.rol, medico_id: s.medico_id || '' });
    setError('');
    setDialogOpen(true);
  }

  async function guardar() {
    if (!form.nombre || !form.email || (!editando && !form.password) || !form.rol) {
      setError('Nombre, email, contraseña y rol son obligatorios');
      return;
    }
    if (form.rol === 'medico' && !form.medico_id) {
      setError('Seleccioná el médico al que corresponde este usuario');
      return;
    }
    setGuardando(true);
    setError('');
    try {
      const payload = { ...form, medico_id: form.medico_id || null };
      if (editando) {
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/api/staff/${editando}`, payload);
      } else {
        await axios.post(`${API}/api/staff`, payload);
      }
      setDialogOpen(false);
      cargarTodo();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  async function desactivar(id) {
    if (!window.confirm('¿Desactivar este usuario? No podrá iniciar sesión.')) return;
    try {
      await axios.delete(`${API}/api/staff/${id}`);
      cargarTodo();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  }

  async function toggleAccesoHC(s) {
    try {
      await axios.put(`${API}/api/staff/${s.id}/acceso-hc`, { acceso_hc_turno: s.acceso_hc_turno ? 0 : 1 });
      cargarTodo();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Usuarios del sistema
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
          Nuevo usuario
        </Button>
      </Box>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      <TableContainer component={Paper} sx={estiloTabla}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Médico vinculado</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Último ingreso</TableCell>
              <TableCell>Actividad</TableCell>
              <TableCell align="center">
                <Tooltip title="Permite al médico cargar evoluciones directamente desde el turno">
                  <span>HC desde turno</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: '#b0b0b0' }}>No hay usuarios</TableCell>
              </TableRow>
            ) : staff.map(s => (
              <TableRow key={s.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' }, opacity: s.activo ? 1 : 0.45 }}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {s.nombre}
                  {s.id === miId && <Chip label="vos" size="small" sx={{ ml: 1, fontSize: 10, height: 18, bgcolor: '#1a3a5c', color: 'var(--c-acento)' }} />}
                </TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>
                  <Chip label={ROL_LABELS[s.rol] || s.rol} color={ROL_COLORS[s.rol] || 'default'} size="small" />
                </TableCell>
                <TableCell>{s.medico_nombre || '—'}</TableCell>
                <TableCell>
                  <Chip label={s.activo ? 'Activo' : 'Inactivo'} color={s.activo ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: '#90a4ae', whiteSpace: 'nowrap' }}>
                  {fmtFechaHora(s.ultimo_login)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const est = estadoOnline(s.ultima_actividad);
                    if (!est) return <Typography sx={{ fontSize: 12, color: '#555' }}>—</Typography>;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: est.color,
                          boxShadow: est.online ? `0 0 6px ${est.color}` : 'none',
                          animation: est.online ? 'pulse 2s infinite' : 'none' }} />
                        <Typography sx={{ fontSize: 12, color: est.color }}>{est.label}</Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="center">
                  {s.rol === 'medico' ? (
                    <Tooltip title={s.acceso_hc_turno ? 'Acceso HC activo — click para desactivar' : 'Sin acceso HC — click para activar'}>
                      <Switch
                        checked={!!s.acceso_hc_turno}
                        onChange={() => toggleAccesoHC(s)}
                        size="small"
                        sx={{ '& .MuiSwitch-thumb': { bgcolor: s.acceso_hc_turno ? '#81c784' : '#555' } }}
                      />
                    </Tooltip>
                  ) : <Typography sx={{ color: '#555', fontSize: 12 }}>—</Typography>}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => abrirEditar(s)} sx={{ color: 'var(--c-acento)' }} size="small" title="Editar">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {s.id !== miId && s.activo ? (
                    <IconButton onClick={() => desactivar(s.id)} sx={{ color: '#f48fb1' }} size="small" title="Desactivar">
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle>{editando ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Nombre *" fullWidth value={form.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email *" type="email" fullWidth value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
                type="password" fullWidth value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel sx={{ color: 'var(--c-acento)' }}>Rol *</InputLabel>
                <Select value={form.rol} label="Rol *"
                  onChange={e => setForm({ ...form, rol: e.target.value, medico_id: '' })}
                  sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="recepcionista">Recepcionista</MenuItem>
                  <MenuItem value="medico">Médico</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.rol === 'medico' && (
              <Grid item xs={12}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel sx={{ color: 'var(--c-acento)' }}>Médico vinculado *</InputLabel>
                  <Select value={form.medico_id} label="Médico vinculado *"
                    onChange={e => setForm({ ...form, medico_id: e.target.value })}
                    sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                    {medicos.map(m => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.nombre} {m.especialidad_nombre ? `— ${m.especialidad_nombre}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: 12, color: '#b0b0b0', mt: 0.5 }}>
                  Este usuario solo verá los turnos del médico seleccionado.
                </Typography>
              </Grid>
            )}
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
